import * as React from 'react';
import { styled, useTheme } from '../theme-provider';
import { createStylesForBreakpoints, getValueForCurrentScreenSize, } from '../helpers/breakpoint';
import { TableNavigation } from './navigation';
const getTableWidth = (columns, columnCount, gutter, tableWidth) => {
    if (columnCount > columns && tableWidth) {
        const width = (((columnCount * (tableWidth - (columns - 1) * gutter)) / columns +
            (columnCount - 1) * gutter) /
            tableWidth) *
            100;
        return `width: ${width}%;`;
    }
    return 'width: 100%;';
};
const createWidthForBreakpoints = (props) => {
    const { columns, columnCount, tableWidth, theme } = props;
    if (!columns || !columnCount || !tableWidth) {
        return 'width: 100%;';
    }
    const gutter = parseInt(theme.size.static350, 10);
    return createStylesForBreakpoints(columns, (columnsPerScreen) => getTableWidth(columnsPerScreen, columnCount, gutter, tableWidth));
};
const StyledTable = styled.table.withConfig({ displayName: "StyledTable", componentId: "sc-1remif3" }) `${createWidthForBreakpoints} font-family:${({ theme }) => theme.fonts.copy[200].fontFamily};color:${({ theme }) => theme.colors.content.primary};background-color:${({ theme }) => theme.colors.background.primary};border-spacing:0px;margin-top:${({ showNavigation, theme, isFixedHeaderRowActive = false, headerHeight = 0, }) => (showNavigation
    ? 2 * parseInt(theme.size.static200, 10) +
        parseInt(theme.size.border.light, 10) +
        parseInt(theme.size.static400, 10)
    : 0) + (isFixedHeaderRowActive ? headerHeight : 0)}px;`;
const StyledTableWrapper = styled.div.withConfig({ displayName: "StyledTableWrapper", componentId: "sc-b3oh0n" }) `width:auto;height:100%;scroll-behavior:smooth;overscroll-behavior-x:contain;overflow-x:scroll;::-webkit-scrollbar{display:none;}scrollbar-width:none;`;
const StyledOuterTableWrapper = styled.div.withConfig({ displayName: "StyledOuterTableWrapper", componentId: "sc-nnnzst" }) `width:auto;height:100%;`;
export const TableContext = React.createContext({
    showNavigation: false,
    tableWidth: 0,
});
export const Table = ({ children, columns = 0, ariaDescribedBy, withNavigation, withOptionalNavigation, fixedHeaderColumn, fixedHeaderRow, stickyHeaderOffset = 0, }) => {
    const tableWrapperRef = React.useRef(null);
    const tableHeadersRef = React.useRef([]);
    const [columnCount, setColumnCount] = React.useState(0);
    const [isFixedHeaderRowActive, setIsFixedHeaderRowActive] = React.useState(false);
    const [headerHeight, setHeaderHeight] = React.useState(0);
    const [tableWidth, setTableWidth] = React.useState(0);
    const [showNavigation, setShowNavigation] = React.useState(true);
    const detectShowNavigation = React.useCallback(() => {
        var _a;
        if (withNavigation) {
            setShowNavigation(true);
            return;
        }
        if (withOptionalNavigation) {
            const currentNumberOfColumns = (_a = getValueForCurrentScreenSize(columns)) !== null && _a !== void 0 ? _a : columnCount;
            setShowNavigation(currentNumberOfColumns < columnCount);
            return;
        }
        setShowNavigation(false);
    }, [columnCount, columns, withNavigation, withOptionalNavigation]);
    const detectTableWidth = React.useCallback(() => {
        const wrapper = tableWrapperRef === null || tableWrapperRef === void 0 ? void 0 : tableWrapperRef.current;
        if (!wrapper) {
            return;
        }
        const width = wrapper.getBoundingClientRect().width;
        setTableWidth(width);
    }, [setTableWidth, tableWrapperRef]);
    const { size: { static200: navigationPadding, static400: buttonHeight, border: { light: borderHeight }, }, } = useTheme();
    const detectFixedHeaderRow = React.useCallback(() => {
        var _a;
        if (!setIsFixedHeaderRowActive) {
            return;
        }
        const wrapper = tableWrapperRef === null || tableWrapperRef === void 0 ? void 0 : tableWrapperRef.current;
        if (!wrapper) {
            return;
        }
        const { top, bottom, height } = wrapper.getBoundingClientRect();
        const navigationHeight = showNavigation
            ? parseInt(navigationPadding, 10) * 2 +
                parseInt(borderHeight, 10) +
                parseInt(buttonHeight, 10)
            : 0;
        if (top <= stickyHeaderOffset - navigationHeight && bottom > height / 2) {
            if (!isFixedHeaderRowActive) {
                const headerHeight = (_a = wrapper
                    .querySelector('thead')) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height;
                setHeaderHeight(headerHeight !== null && headerHeight !== void 0 ? headerHeight : 0);
                setIsFixedHeaderRowActive(true);
            }
        }
        else {
            if (isFixedHeaderRowActive) {
                setIsFixedHeaderRowActive(false);
            }
        }
    }, [
        borderHeight,
        buttonHeight,
        isFixedHeaderRowActive,
        navigationPadding,
        showNavigation,
        stickyHeaderOffset,
    ]);
    // detect fixed header row on window scroll
    React.useEffect(() => {
        if (fixedHeaderRow) {
            window.addEventListener('scroll', detectFixedHeaderRow);
        }
        return () => window.removeEventListener('scroll', detectFixedHeaderRow);
    }, [detectFixedHeaderRow, tableWrapperRef, fixedHeaderRow]);
    const handleResize = React.useCallback(() => {
        detectTableWidth();
        detectShowNavigation();
    }, [detectShowNavigation, detectTableWidth]);
    // detect table width and navigation on window resize
    React.useEffect(() => {
        const localHandleResize = handleResize;
        window.addEventListener('resize', localHandleResize);
        return () => window.removeEventListener('resize', localHandleResize);
    }, [handleResize]);
    // initially detect table width and navigation
    React.useEffect(() => {
        handleResize();
    }, [handleResize]);
    // detect table width and navigation when table becomes visible when it is initially hidden
    React.useEffect(() => {
        const wrapper = tableWrapperRef === null || tableWrapperRef === void 0 ? void 0 : tableWrapperRef.current;
        if (!wrapper) {
            return;
        }
        const observer = new ResizeObserver((entries, _observer) => {
            entries.forEach((entry) => {
                if (entry.contentRect.width > 0) {
                    handleResize();
                }
            });
        });
        observer.observe(wrapper);
        return () => {
            observer.unobserve(wrapper);
            observer.disconnect(); // this is probably not necessary, but just in case ...
        };
    }, [handleResize, tableWrapperRef]);
    const tableContextValue = React.useMemo(() => ({
        tableHeadersRef,
        tableWrapperRef,
        columnCount,
        setColumnCount,
        isFixedHeaderRowActive,
        showNavigation,
        fixedHeaderColumn,
        tableWidth,
        stickyHeaderOffset,
    }), [
        columnCount,
        fixedHeaderColumn,
        isFixedHeaderRowActive,
        showNavigation,
        stickyHeaderOffset,
        tableWidth,
    ]);
    return (React.createElement(TableContext.Provider, { value: tableContextValue },
        React.createElement(StyledOuterTableWrapper, null,
            showNavigation && React.createElement(TableNavigation, { columns: columns }),
            React.createElement(StyledTableWrapper, { ref: tableWrapperRef },
                React.createElement(StyledTable, { "aria-describedby": ariaDescribedBy, showNavigation: showNavigation, isFixedHeaderRowActive: isFixedHeaderRowActive, headerHeight: headerHeight, columns: columns, tableWidth: tableWidth, columnCount: columnCount, tabIndex: 0 },
                    React.createElement(React.Fragment, null, children))))));
};
Table.displayName = 'Table';
const StyledCaption = styled.caption.withConfig({ displayName: "StyledCaption", componentId: "sc-muolur" }) `height:${({ theme }) => theme.size.static510};`;
export const TableCaption = ({ children }) => React.createElement(StyledCaption, null, children);
export const useTableContext = () => React.useContext(TableContext);
